import React from 'react';
import { AccordionItem, AccordionContent } from '@/components/ui/accordion';
import PieGraph from '@/components/organisms/graphs/PieGraph';
import chevronDownIcon from '@/assets/icons/chevron-down.svg';
import revenueIcon from '@/assets/icons/revenue.svg';
import { useNavigate, useParams } from 'react-router-dom';

interface RCItemAccordionProps {
  rc: any;
  graphViewMode: string;
  open: boolean;
  onToggle: () => void;
}

const RCItemAccordion = ({ rc, graphViewMode, open, onToggle }: RCItemAccordionProps) => {
  const navigate = useNavigate();
  const { budgetId } = useParams();

  // Calculate chart data for this specific revenue category
  const rcrtRemaining = rc.rcrt - rc.rcrt_achieved || 0;
  const rcrtAchieved = rc.rcrt_achieved || 0;
  const rcrtTotal = rcrtRemaining + rcrtAchieved;

  const rcrtChartData = [
    {
      key: 'target',
      value: rcrtTotal === 0 ? 100 : Math.round((rcrtRemaining / rcrtTotal) * 100),
      fill: '#32ADE6'
    },
    {
      key: 'achieved',
      value: rcrtTotal === 0 ? 0 : Math.round((rcrtAchieved / rcrtTotal) * 100),
      fill: '#48C80C'
    }
  ];

  const rcrtChartConfig = {
    target: { label: 'Revenue Target', color: '#32ADE6' },
    achieved: { label: 'Achieved Revenue', color: '#48C80C' }
  };

  return (
    <AccordionItem
      value={rc._id}
      className="bg-white md:bg-transparent border rounded-[8px] min-h-[88px] transition-all duration-300"
    >
      <div className="flex select-none py-0 pr-3 min-w-0">
        <div className="shrink-0 min-w-[40px] px-5 py-6 flex items-center justify-center">
          <img className="w-[40px] h-[40px] shrink-0" src={revenueIcon} alt="revenue-icon" />
        </div>
        <div className="flex-1 min-w-0 flex justify-center flex-col gap-[0.4rem] border-l p-6 py-4 text-left">
          <button
            title={rc.name || '-'}
            onClick={() => {
              navigate(`/budgets/${budgetId}/revenue-categories/${rc._id}`, {
                state: { rc }
              });
            }}
            className="font-bold text-sm md:text-base leading-[110%] capitalize truncate mb-[0.1rem] cursor-pointer text-left w-full min-w-0 block"
          >
            {rc.name || '-'}
          </button>
          <span className="text-xs text-secondary leading-[100%]">
            Achieved RCRT €{rcrtAchieved?.toLocaleString() || '0'}
          </span>
        </div>

        <div className="shrink-0 flex items-center">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            className={`w-[26px] h-[26px] flex items-center justify-center border rounded-full bg-lightPurple-200 ml-2 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
            aria-label={open ? 'Collapse' : 'Expand'}
            aria-expanded={open}
          >
            <img src={chevronDownIcon} alt="toggle" />
          </button>
        </div>
      </div>
      <AccordionContent className="border-t" style={{ display: open ? 'block' : 'none' }}>
        <div className="p-4 pb-0">
          <div className="flex items-center justify-between gap-4">
            <div className="flex flex-col gap-1">
              <p className="text-sm text-secondary">Achieved RCRT</p>
              <p className="text-[20px] font-bold leading-[24px] mb-2">
                {graphViewMode === 'percentage' ? `${rcrtChartData[1].value}%` : `€${rcrtAchieved.toLocaleString()}`}
              </p>

              <div className="mt-3 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="w-[9px] h-[9px] rounded-full bg-[#32ADE6]"></span>
                  <p className="text-xs text-secondary">Revenue Target</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-[9px] h-[9px] rounded-full bg-[#48C80C]"></span>
                  <p className="text-xs text-secondary">Achieved Revenue</p>
                </div>
              </div>
            </div>

            <div className="flex-shrink-0">
              <PieGraph
                title="Achieved RCRT"
                titleValue={
                  graphViewMode === 'percentage' ? `${rcrtChartData[1].value}%` : `€${rcrtAchieved.toLocaleString()}`
                }
                chartConfig={rcrtChartConfig}
                chartData={rcrtChartData}
                className=""
                outerRadius={55}
                minHeight={110}
                hideLabel={true}
              />
            </div>
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};

export default RCItemAccordion;
